import React, { useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Grid, InputAdornment, TextField } from "@material-ui/core";
import { AutocompleteComponent } from "components/Autocomplete/AutocompleteComponent";
import { ModalV2 } from "components/Modal/ModalV2";
import { addLabelArray } from "helpers/addLabelArray";
import { formatNumber } from "helpers/getNumberFormats";
import { useForm } from "hooks/useForm";
import { saveShipment } from "redux/actions/inventories/inventShipmentsActions";

const shipmentValidations = {
  selectedStorage: {
    required: true,
  },
  selectedCarrier: {
    required: true,
  },
  selectedShipperTruck: {
    required: true,
  },
  initialPressure: {
    required: true,
  },
  finalPressure: {
    required: true,
  },
  initialTemperature: {
    required: true,
  },
  finalTemperature: {
    required: true,
  },
  initialRotogauge: {
    required: true,
  },
  finalRotogauge: {
    required: true,
  }
};

const errorMsgSpace = " ";
const formSpacing = { paddingTop: 0, paddingBottom: 4 };

// Campos informativos: siempre deshabilitados.
const infoFields = [
  { label: "Número de embarque", name: "shipmentNumber", textAlign: "left" },
  { label: "Kilos", name: "kilograms", unit: "kg" },
];

// Campos de selección: comparten estructura (TextField en solo lectura,
// Autocomplete en edición) y solo varían en sus datos/opciones.
// `readOnlyName` coincide con la clave correspondiente en `data`.
// `optionsKey` referencia el conjunto de opciones resuelto en el componente.
const selectFields = [
  {
    title: "Tanque (Permiso CRE)",
    name: "selectedStorage",
    readOnlyName: "creLicense",
    keyOption: "value",
    optionsKey: "selectedStorage",
  },
  {
    title: "Transportista",
    name: "selectedCarrier",
    readOnlyName: "carrier",
    keyOption: "name",
    optionsKey: "selectedCarrier",
    sort: true,
  },
  {
    title: "PG",
    name: "selectedShipperTruck",
    readOnlyName: "shipperTruck",
    keyOption: "value",
    optionsKey: "selectedShipperTruck",
  },
];

// Campos de medición: editables salvo que el embarque sea de solo lectura.
// `alwaysDisabled` marca los que nunca se editan desde el formulario.
const measureFields = [
  { label: "Presión inicial", name: "initialPressure" },
  { label: "Presión final", name: "finalPressure" },
  { label: "Temperatura inicial", name: "initialTemperature", unit: "°C" },
  { label: "Temperatura final", name: "finalTemperature", unit: "°C" },
  { label: "Rotogauge inicial", name: "initialRotogauge" },
  { label: "Rotogauge final", name: "finalRotogauge" },
  { label: "Litros descargados", name: "dischargedLiters", unit: "lt", sm: 12, alwaysDisabled: true },
];

const FormTextField = ({
  label,
  name,
  value,
  unit = "",
  textAlign = "right",
  onChange,
  error,
  sm = 6,
  disabled = false,
}) => (
  <Grid item xs={12} sm={sm} style={formSpacing}>
    <TextField
      label={label}
      name={name}
      value={value}
      onChange={onChange}
      fullWidth
      variant="outlined"
      InputProps={
        unit
          ? {
              endAdornment: (
                <InputAdornment style={{ marginLeft: 8 }} position="end">
                  {unit}
                </InputAdornment>
              )
            }
          : undefined
      }
      inputProps={{ style: { textAlign } }}
      error={!!error}
      helperText={error || errorMsgSpace}
      disabled={disabled}
    />
  </Grid>
);

// Campo de selección (solo edición): muestra un AutocompleteComponent.
// En solo lectura se reutiliza FormTextField como TextField deshabilitado.
const AutocompleteField = ({
  title,
  name,
  keyOption,
  value,
  error,
  options,
  sort = false,
  handleValues,
}) => (
  <Grid item xs={12} style={formSpacing}>
    <AutocompleteComponent
      label={title}
      name={name}
      keyOption={keyOption}
      value={value}
      handleValues={handleValues}
      fullWidth
      variant="outlined"
      textFieldProps={{
        error: !!error,
        helperText: error || errorMsgSpace,
      }}
      options={options}
      sort={sort}
    />
  </Grid>
);

const ShipmentForm = ({
  data = {},
  setOpen = () => {},
  open = false,
  title,
  isReadOnly = false,
}) => {
  const dispatch = useDispatch();
  const { inventory, carries, shipperTrucks } = useSelector(({ inventories }) => inventories);
  const { isLoadingComp } = useSelector(({ search }) => search);
  const storagesInventory = inventory.details?.storagesInventory || [];
  const storagesOptions = useMemo(
    () => addLabelArray(storagesInventory, 'number', 'creLicense'),
    [storagesInventory]
  );
  const shipperTrucksOptions = useMemo(
    () => addLabelArray(shipperTrucks, 'license', 'type'),
    [shipperTrucks]
  );

  // Opciones por campo, resueltas en runtime desde el store.
  const optionsByField = {
    selectedStorage: storagesOptions,
    selectedCarrier: carries,
    selectedShipperTruck: shipperTrucksOptions,
  };

  const initialValues = {
    shipmentNumber: data.shipmentNumber || "",
    kilograms: formatNumber({ input: data.kilograms }),
    selectedStorage: null,
    selectedCarrier: null,
    selectedShipperTruck: null,
    initialPressure: String(data.initialPressure ?? "0"),
    initialPressure_: formatNumber({ input: data.initialPressure }),
    finalPressure: String(data.finalPressure ?? "0"),
    finalPressure_: formatNumber({ input: data.finalPressure }),
    initialTemperature: String(data.initialTemperature ?? "0"),
    initialTemperature_: formatNumber({ input: data.initialTemperature }),
    finalTemperature: String(data.finalTemperature ?? "0"),
    finalTemperature_: formatNumber({ input: data.finalTemperature }),
    initialRotogauge: String(data.initialRotogauge ?? "0"),
    initialRotogauge_: formatNumber({ input: data.initialRotogauge }),
    finalRotogauge: String(data.finalRotogauge ?? "0"),
    finalRotogauge_: formatNumber({ input: data.finalRotogauge }),
  };

  const {
    values,
    setValues,
    errors,
    onChangeNumberV2,
    validate,
  } = useForm(initialValues, shipmentValidations);

  const {
    selectedStorage,
    selectedCarrier,
    selectedShipperTruck,
    initialPressure,
    finalPressure,
    initialTemperature,
    finalTemperature,
    initialRotogauge,
    finalRotogauge,
  } = values;

  const initialRotogaugeNum = Number(initialRotogauge);
  const finalRotogaugeNum = Number(finalRotogauge);

  const computedDischargedLiters = useMemo(() => {
    if (isReadOnly) return data.dischargedLiters ?? 0;
    const capacity = selectedShipperTruck?.capacity || 0;
    const result = capacity * (initialRotogaugeNum - finalRotogaugeNum) * (1 / 90);
    return Math.round(result * 100) / 100;
  }, [isReadOnly, data.dischargedLiters, selectedShipperTruck?.capacity, initialRotogaugeNum, finalRotogaugeNum]);

  // Overrides de valor para campos calculados (no provienen del input directo).
  const valueOverrides = { dischargedLiters: computedDischargedLiters };

  // De acuerdo con el cálculo en computedDischargedLiters, initialRotogauge > finalRotogauge
  const isRotogaugeInvalid = initialRotogaugeNum < finalRotogaugeNum;
  const errorOverrides = {
    finalRotogauge: isRotogaugeInvalid
      ? "* El rotogauge final NO puede ser mayor que el inicial"
      : "",
  };

  const handleSubmit = () => {
    if (isRotogaugeInvalid) return;
    validate([], async () => {
      const form = {
        idInventory: inventory.id,
        idCarrier: selectedCarrier?.id || null,
        carrier: selectedCarrier?.name || null,
        idShipperTruck: selectedShipperTruck?.id || null,
        shipperTruck: selectedShipperTruck?.license || null,
        creLicense: selectedStorage?.creLicense || null,
        initialPressure: Number(initialPressure),
        finalPressure: Number(finalPressure),
        initialTemperature: Number(initialTemperature),
        finalTemperature: Number(finalTemperature),
        initialRotogauge: initialRotogaugeNum,
        finalRotogauge: finalRotogaugeNum,
        dischargedLiters: Number(computedDischargedLiters),
      };
      const isSaved = await dispatch(saveShipment({ shipmentId: data.id, form }));
      if (isSaved) setOpen();
    });
  };

  return (
    <ModalV2
      title={title}
      open={open}
      onClose={setOpen}
      onSubmit={isReadOnly ? null : handleSubmit}
      submitBtnText="Guardar"
      isLoading={isLoadingComp?.saveShipment}
      toRight
    >
      {infoFields.map(({ label, name, unit, textAlign }) => (
        <FormTextField
          key={name}
          label={label}
          name={name}
          value={values[name]}
          unit={unit}
          textAlign={textAlign}
          error={errors[name]}
          disabled
        />
      ))}
      {selectFields.map(({ name, readOnlyName, optionsKey, ...field }) =>
        isReadOnly ? (
          <FormTextField
            key={name}
            label={field.title}
            name={readOnlyName}
            value={data[readOnlyName] || ""}
            sm={12}
            textAlign="left"
            disabled
          />
        ) : (
          <AutocompleteField
            key={name}
            name={name}
            {...field}
            value={values[name]}
            error={errors[name]}
            options={optionsByField[optionsKey]}
            handleValues={setValues}
          />
        )
      )}
      {measureFields.map(({ label, name, unit, textAlign, sm, alwaysDisabled }) => (
        <FormTextField
          key={name}
          label={label}
          name={name}
          value={
            name in valueOverrides
              ? formatNumber({ input: valueOverrides[name] })
              : (values[`${name}_`] || values[name])
          }
          unit={unit}
          textAlign={textAlign}
          sm={sm}
          onChange={(e) => onChangeNumberV2(e, { decimals: 2 })}
          error={errors[name] || errorOverrides[name]}
          disabled={alwaysDisabled || isReadOnly}
        />
      ))}
    </ModalV2>
  );
};

export default ShipmentForm;
